import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';

import {
  type PromoFeatureAccess,
  type PromoFeatureAccessReason,
  type PromoFeaturesConfig,
  evaluateFeatureRequiredRoles,
} from '@luxuryescapes/contract-svc-promo';

import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';

import { type PromoFeatureIndicator } from './promoFeatureIndicators';

interface Props {
  feature: PromoFeatureIndicator;
  config: PromoFeaturesConfig;
  canToggleOptIn: boolean;
  optedInFeatureKeys: ReadonlySet<string>;
  onSetOptedIn: (featureKey: string, optedIn: boolean) => void;
}

function requirementLabel(requirement: PromoFeatureAccessReason['requirement']): string {
  switch (requirement.type) {
    case 'auth_role':
      return `Admin role: ${requirement.key}`;
    case 'promo_role':
      return `Promo role: ${requirement.key}`;
    case 'optIn':
      return `Session opt-in: ${requirement.key}`;
    case 'promoBrand':
      return `Brands: ${requirement.brands.join(', ') || 'None'}`;
    case 'region':
      return `Regions: ${requirement.regions.join(', ') || 'None'}`;
    case 'testEnvironment':
      return 'Test environment';
    case 'publicFeature':
      return 'Public feature access';
  }
}

function accessStatus(access: PromoFeatureAccess) {
  if (access === 'unknown') {
    return { color: 'warning' as const, icon: <HelpOutlineIcon />, label: 'Unknown' };
  }
  return access
    ? { color: 'success' as const, icon: <CheckCircleOutlineIcon />, label: 'Met' }
    : { color: 'error' as const, icon: <HighlightOffIcon />, label: 'Missing' };
}

export default function PromoFeatureRoleAccess({
  feature,
  config,
  canToggleOptIn,
  optedInFeatureKeys,
  onSetOptedIn,
}: Props) {
  const { promoBrand } = useCurrentPromoBrand();
  const context = {
    brand: promoBrand,
    authRoles: config.authRoles === 'unknown' ? undefined : config.authRoles,
    promoRoles: config.promoRoles === 'unknown' ? undefined : config.promoRoles,
    optIns: config.optIns === 'unknown' ? undefined : config.optIns,
  };
  const requirements = feature.requiredRoles?.roles ?? [];
  const directParentKey = feature.parentKeys[feature.parentKeys.length - 1];
  const parent = directParentKey ? config.features.find((entry) => entry.key === directParentKey) : undefined;
  const parentAccessIssue = parent && parent.canUse !== true ? accessStatus(parent.canUse) : undefined;

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {requirements.length > 1 && (
        <Typography variant="caption" color="text.secondary">
          {feature.requiredRoles?.operator === 'or' ? 'Any of:' : 'All of:'}
        </Typography>
      )}
      {requirements.map((requirement) => {
        if (
          requirement.type === 'optIn' &&
          config.features.some((entry) => entry.key === requirement.key && entry.canOptIn)
        ) {
          const isOptedIn = optedInFeatureKeys.has(requirement.key);
          const actionLabel = `${isOptedIn ? 'Turn off' : 'Turn on'} session opt-in for ${feature.displayName}`;

          return canToggleOptIn ? (
            <Tooltip key={`${requirement.type}:${requirement.key}`} title={actionLabel}>
              <Chip
                size="small"
                color={isOptedIn ? 'success' : 'default'}
                label={isOptedIn ? 'Opted in for this session' : 'Opt in for this session'}
                onClick={(event) => {
                  event.stopPropagation();
                  onSetOptedIn(requirement.key, !isOptedIn);
                }}
                aria-label={actionLabel}
                aria-pressed={isOptedIn}
                variant="outlined"
              />
            </Tooltip>
          ) : (
            <Chip key={`${requirement.type}:${requirement.key}`} size="small" label="Promo beta testers only" />
          );
        }

        const access = evaluateFeatureRequiredRoles({ operator: 'and', roles: [requirement] }, context);
        const status = accessStatus(access);

        return (
          <Chip
            key={`${requirement.type}:${requirement.key}`}
            size="small"
            color={status.color}
            icon={status.icon}
            label={`${status.label}: ${requirementLabel(requirement)}`}
            variant="outlined"
          />
        );
      })}
      {feature.accessReasons
        .filter((reason) =>
          ['promoBrand', 'region', 'testEnvironment', 'publicFeature'].includes(reason.requirement.type),
        )
        .map((reason, index) => {
          const status = accessStatus(reason.result);
          return (
            <Chip
              key={`${reason.featureKey}:${reason.requirement.type}:${index}`}
              size="small"
              color={status.color}
              icon={status.icon}
              label={`${status.label}: ${requirementLabel(reason.requirement)}`}
              variant="outlined"
            />
          );
        })}
      {parent && parentAccessIssue && (
        <Chip
          size="small"
          color={parentAccessIssue.color}
          icon={parentAccessIssue.icon}
          label={
            parent.canUse === 'unknown'
              ? `Parent feature access unknown: ${parent.displayName}`
              : `Requires access to parent feature: ${parent.displayName}`
          }
          variant="outlined"
        />
      )}
      {requirements.length === 0 && !parentAccessIssue && (
        <Chip size="small" color="success" label={parent ? 'No additional role required' : 'No role required'} />
      )}
    </Stack>
  );
}
